// apps/merchant-v3/src/layouts/Breadcrumb.tsx
//
// Phase 6 Wave 1 Task 02 — TopBar breadcrumb derived from current pathname
// . Walks the URL segments and looks up a friendly label per
// segment from a static map; falls back to title-cased segment when
// missing. Real route-aware breadcrumb (driven by router match handles)
// can replace this in a later wave.
//
// ── Tab crumbs ─────────────────────────────────────────────────────────────
//
// Several pages are hubs whose sections live in `?tab=` rather than in the
// path (Wallet, Notifications, Branding, Account, Store detail, Billing
// settings). Reading the pathname alone, every one of those sections
// rendered the same crumb, so the breadcrumb could not tell the merchant
// which section they were in — the thing a breadcrumb exists to say.
//
// `TAB_CRUMBS` below maps a pathname to the labels for its tabs. It keeps
// page knowledge in the layout, which is not where it belongs long-term; a
// context the hubs push into would be cleaner. It is deliberate for now:
// this file is already the stop-gap its own header describes, and a declarative
// table in one file is a smaller stop-gap than a provider plus six edits.
//
// NOTE: a route `handle` — the usual answer — cannot work in this app.
// `main.tsx` mounts a single splat route (`path: '*'`) with the real route
// tree nested inside, so `useMatches()` returns one match carrying no handle.
//
// vg-binding: PLAN:task-02
// vg-binding: P6.D-05

import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Maps a URL segment to an i18n key + English default. Keys point at the
// shared `navigation.*` / `actions.*` namespaces when a translation already
// exists, and at the dedicated `breadcrumb.*` namespace for segments with no
// existing equivalent.
const SEGMENT_LABEL_KEYS: Record<string, { key: string; en: string }> = {
  dashboard: { key: 'navigation.dashboard', en: 'Dashboard' },
  orders: { key: 'navigation.orders', en: 'Orders' },
  create: { key: 'actions.create', en: 'Create' },
  import: { key: 'breadcrumb.import', en: 'Import' },
  products: { key: 'navigation.products', en: 'Products' },
  my: { key: 'navigation.sidebar.myProducts', en: 'My Products' },
  artwork: { key: 'breadcrumb.artwork', en: 'Artwork Library' },
  stores: { key: 'navigation.stores', en: 'Stores' },
  'sync-logs': { key: 'navigation.sidebar.syncLogs', en: 'Sync Logs' },
  wallet: { key: 'navigation.wallet', en: 'Wallet' },
  topup: { key: 'breadcrumb.topup', en: 'Top-up' },
  transactions: { key: 'breadcrumb.transactions', en: 'Transactions' },
  billing: { key: 'navigation.sidebar.billing', en: 'Billing' },
  invoices: { key: 'navigation.invoices', en: 'Invoices' },
  // Reached as /billing/payment-methods and /billing/payment-requests; without
  // these the fallback title-cases the slug, which leaves English in a vi/fr UI.
  'payment-methods': { key: 'breadcrumb.paymentMethods', en: 'Payment Methods' },
  'payment-requests': { key: 'breadcrumb.paymentRequests', en: 'Payment Requests' },
  team: { key: 'navigation.team', en: 'Team' },
  settings: { key: 'navigation.settings', en: 'Settings' },
  branding: { key: 'navigation.branding', en: 'Branding' },
  profile: { key: 'navigation.sidebar.profile', en: 'Profile' },
  notifications: { key: 'navigation.notifications', en: 'Notifications' },
  tickets: { key: 'navigation.tickets', en: 'Tickets' },
  onboarding: { key: 'breadcrumb.onboarding', en: 'Onboarding' },
  login: { key: 'breadcrumb.login', en: 'Sign in' },
};

/**
 * Tab labels per hub page, keyed by the pathname with id segments collapsed
 * to `:id`.
 *
 * `defaultTab` is the section that renders on the bare path, and gets NO
 * crumb: `/wallet` and `/wallet?tab=overview` are the same screen, so
 * "Wallet › Overview" would just repeat the page it is already standing on.
 *
 * Tab ids are copied verbatim from each hub — they are a wire value, not a
 * label, and the three hubs disagree on casing (`paymentMethods`,
 * `auto-topup`, `all`). Deriving a label from the id would be wrong for two
 * of the three, so every one is listed.
 *
 * `ns` names the i18n namespace when it is not `common`; `param` names the
 * query key when it is not `tab`.
 */
const TAB_CRUMBS: Record<
  string,
  {
    defaultTab: string;
    ns?: string;
    param?: string;
    tabs: Record<string, string>;
  }
> = {
  // pages/wallet/WalletOverviewPage.tsx
  '/wallet': {
    defaultTab: 'overview',
    tabs: {
      overview: 'wallet.pageTab.overview',
      billing: 'wallet.pageTab.billing',
      invoices: 'wallet.pageTab.invoices',
      transactions: 'wallet.pageTab.transactions',
      paymentMethods: 'wallet.pageTab.paymentMethods',
      billingSettings: 'wallet.pageTab.billingSettings',
      paymentRequests: 'wallet.pageTab.paymentRequests',
    },
  },
  // pages/notifications/NotificationsCenterPage.tsx
  '/notifications': {
    defaultTab: 'all',
    tabs: {
      all: 'notifications.tab.all',
      orders: 'notifications.tab.orders',
      products: 'notifications.tab.products',
      stores: 'notifications.tab.stores',
      wallet: 'notifications.tab.wallet',
      system: 'notifications.tab.system',
    },
  },
  // pages/settings/SettingsBrandingPage.tsx
  '/settings/branding': {
    defaultTab: 'brand-identity',
    tabs: {
      'brand-identity': 'branding.tab.brandIdentity',
      'thank-you': 'branding.tab.thankYouCards',
      'gift-messages': 'branding.tab.giftMessages',
      packaging: 'branding.tab.packaging',
    },
  },
  // pages/settings/AccountSettingsPage.tsx — the one hub that keeps
  // `?tab=profile` on the URL rather than deleting it, so the default tab
  // is reachable with and without the param. Both render the same crumb.
  '/settings/profile': {
    defaultTab: 'profile',
    tabs: {
      profile: 'settings.tab.profile',
      security: 'settings.tab.security',
      notifications: 'settings.tab.notifications',
      preferences: 'settings.tab.preferences',
      activity: 'settings.tab.activity',
    },
  },
  // pages/stores/StoreDetailPage.tsx
  '/stores/:id': {
    defaultTab: 'overview',
    tabs: {
      overview: 'stores.detail.tab.overview',
      mapping: 'stores.detail.tab.mapping',
      'sync-logs': 'stores.detail.tab.syncLogs',
      settings: 'stores.detail.tab.settings',
    },
  },
  // pages/billing/Settings/SettingsPage.tsx — reads `?settingsTab=`, not
  // `?tab=`, because the Wallet hub owns `?tab=` while this page is embedded
  // in it. Stated here so the crumb reads the key the page actually uses.
  '/billing/settings': {
    defaultTab: 'alerts',
    ns: 'billing',
    param: 'settingsTab',
    tabs: {
      alerts: 'settingsTabs.tabs.alerts',
      'auto-topup': 'settingsTabs.tabs.autoTopup',
      history: 'settingsTabs.tabs.history',
      receipts: 'settingsTabs.tabs.receipts',
      tax: 'settingsTabs.tabs.tax',
    },
  },
};

function titleCase(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// dbg-20260704 (notif breadcrumb) — a dynamic detail segment (Mongo
// ObjectId, UUID, or a bare numeric id) has no friendly label in the map,
// so titleCase rendered the raw id (e.g. `/notifications/665f…`). Detect
// id-shaped segments and label them generically as "Detail".
function isIdSegment(segment: string): boolean {
  return (
    /^[0-9a-f]{24}$/i.test(segment) || // Mongo ObjectId
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      segment,
    ) || // UUID
    /^\d+$/.test(segment) // numeric id
  );
}

/**
 * Collapse id-shaped segments so `/stores/665f…` matches the `/stores/:id`
 * entry. Reuses `isIdSegment`, which the label path already trusts for the
 * same job, so a path cannot be an id here and a name there.
 */
function toTabCrumbKey(segments: readonly string[]): string {
  return '/' + segments.map((s) => (isIdSegment(s) ? ':id' : s)).join('/');
}

export function Breadcrumb(): JSX.Element {
  const { t } = useTranslation('common');
  // A second, namespace-free `t` so a hub whose labels live elsewhere
  // (billing) can be read without pinning this component to one namespace.
  const { t: tAny } = useTranslation();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const segments = pathname.split('/').filter(Boolean);

  const labelize = (segment: string): string => {
    const entry = SEGMENT_LABEL_KEYS[segment];
    if (entry) return t(entry.key, { defaultValue: entry.en });
    if (isIdSegment(segment)) {
      return t('breadcrumb.detail', { defaultValue: 'Detail' });
    }
    return titleCase(segment);
  };

  const crumbs: { label: string; href: string; isTab?: boolean }[] = segments.map(
    (seg, idx) => {
      const href = '/' + segments.slice(0, idx + 1).join('/');
      return { label: labelize(seg), href };
    },
  );

  // Append the active tab, when this path is a hub and the tab is not its
  // default. An unknown tab id adds nothing — the hub itself falls back to
  // its default in that case, so inventing a crumb would describe a screen
  // the merchant is not looking at.
  const hub = TAB_CRUMBS[toTabCrumbKey(segments)];
  if (hub) {
    const active = searchParams.get(hub.param ?? 'tab') ?? hub.defaultTab;
    const labelKey = hub.tabs[active];
    if (labelKey && active !== hub.defaultTab) {
      crumbs.push({
        label: hub.ns
          ? tAny(labelKey, { ns: hub.ns, defaultValue: labelKey })
          : t(labelKey, { defaultValue: labelKey }),
        // Not a link: it is the page being viewed, same as every other
        // final crumb. The href only has to be unique for React's key.
        href: `${pathname}?${hub.param ?? 'tab'}=${active}`,
        isTab: true,
      });
    }
  }

  return (
    <nav aria-label={t('a11y.breadcrumb')} className="flex items-center text-sm">
      <ol className="flex items-center gap-1.5">
        <li>
          <Link
            to="/dashboard"
            className="text-pw-text-muted hover:text-pw-text"
          >
            {t('breadcrumb.home', { defaultValue: 'Home' })}
          </Link>
        </li>
        {crumbs.map((c, idx) => {
          // A tab crumb is never a link even if something were appended
          // after it: it names a section of the page already on screen.
          const isLast = idx === crumbs.length - 1 || c.isTab === true;
          return (
            <li key={c.href} className="flex items-center gap-1.5">
              <ChevronRight
                className="h-3.5 w-3.5 text-pw-text-muted"
                aria-hidden
              />
              {isLast ? (
                <span aria-current="page" className="font-medium text-pw-text">
                  {c.label}
                </span>
              ) : (
                <Link
                  to={c.href}
                  className="text-pw-text-muted hover:text-pw-text"
                >
                  {c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
